import * as React from 'react'
import { useQuery } from '@apollo/react-hooks'
import gql from 'graphql-tag'
import { useAmp } from 'next/amp'

import { ContentHeader } from '@thg-commerce/gravity-patterns'
import { ContentHeaderSearchProps } from '@thg-commerce/gravity-patterns/ContentHeader/ContentHeaderSearch/ContentHeaderSearch'
import {
  getContentRoutePathname,
  i18n,
  Routes,
} from '@thg-commerce/enterprise-core'
import { NetworkClient } from '@thg-commerce/enterprise-network'
import { MenuLocationEnum } from '@thg-commerce/enterprise-network/src/generated/Content/graphql'

import { FullHeaderLayout } from '../'
import { useSiteDefinition } from '../../ConfigurationLoader'

export const MENUS_QUERY = gql`
  query nav {
    menus @client {
      text
      path
    }
  }
`

interface RemoteMenuStructure {
  menus: {
    text: string
    path: string
  }[]
}

export const ContentHeaderLayout = ({
  children,
  pageLayoutData,
}: {
  children: React.ReactNode
  pageLayoutData?: any
}) => {
  const isAmp = useAmp()
  const { contentRoutePrefix } = useSiteDefinition()

  const { loading, data } = useQuery(MENUS_QUERY, {
    context: { clientName: NetworkClient.Content },
    variables: {
      where: { location: MenuLocationEnum.PrimaryNav },
    },
  })

  const i18nText = {
    latest: i18n('contentheader.latest.text'),
    ariaLabel: i18n('contentheader.arialabel'),
  }

  const searchUrl = getContentRoutePathname(
    contentRoutePrefix,
    Routes.BlogSearch,
  )
  const contentHeaderSearchProps: ContentHeaderSearchProps = {
    I18nText: {
      buttonTitle: i18n('header.contentsearchbutton.title'),
      formPlaceholder: i18n('header.contentsearchform.placeholder'),
      formTitle: i18n('header.contentsearchform.title'),
    },
    searchUri:
      searchUrl.charAt(searchUrl.length - 1) === '/'
        ? searchUrl.slice(0, -1)
        : searchUrl,
  }

  const buildNavigationStructure = (data: RemoteMenuStructure) => {
    return data?.menus.map((item) => {
      return {
        text: item.text,
        path: item.path,
      }
    })
  }

  return (
    <FullHeaderLayout
      features={{
        stickyHeader: false,
        showStripBanner: false,
        wishlistNotification: false,
      }}
      beforeMainContent={
        <ContentHeader
          navigationStructure={
            (!loading && buildNavigationStructure(data)) || undefined
          }
          isAmp={isAmp}
          contentHeaderSearch={contentHeaderSearchProps}
          i18nText={i18nText}
          contentHomeURI={getContentRoutePathname(
            contentRoutePrefix,
            Routes.Blog,
          )}
        />
      }
      pageLayoutData={pageLayoutData}
      $window={typeof window !== 'undefined' ? window : undefined}
    >
      {children}
    </FullHeaderLayout>
  )
}
